import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ArrowRight, Phone, Navigation, Clock, ShieldCheck, Cpu, Info, MemoryStick, HardDrive, MonitorIcon, Tag, ChevronLeft, ChevronRight, Loader2, Heart, PackageCheck, PackageOpen, Truck, FileText, Sparkles, FileDown, Minus, Plus, ShoppingCart, Check, Edit3, CheckCircle2, Clock4, XCircle, Bell } from 'lucide-react';
import { addToWishlist, BUSINESS, getDisplayWarranty, getHeroImageForProduct, getImageUrl, getProduct, getProductIds, getWishlist, joinProductWaitingList, RDV_CONSEIL_URL, removeFromWishlist, setProductSaleStatus } from '../config';
import { recordProductView, writeJourneyCookie } from '../hooks/useBrowseJournal';
import QuoteRequestModal from '../components/QuoteRequestModal';
import ComingSoonOverlay from '../components/ComingSoonOverlay';
import ProductAlertForm from '../components/ProductAlertForm';
import { useAuth } from '../context/AuthContext';
import { maxQtyFor, useCart } from '../context/CartContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const STATUS_MAP = {
  available: { label: 'Disponible', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  reserved: { label: 'Réservé', cls: 'bg-slate-500/15 text-slate-300 border-slate-500/30' },
  sold: { label: 'Vendu', cls: 'bg-slate-500/15 text-slate-400 border-slate-500/30' },
};

export default function ProductDetail({ maintenance }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuoteRequest, setShowQuoteRequest] = useState(false);
  const [quantity, setQuantity] = useState(1);
  // null | 'added' (le panier a changé) | 'max' (quantité déjà au maximum).
  const [addOutcome, setAddOutcome] = useState(null);
  const [currentImage, setCurrentImage] = useState(0);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState(null);
  const [neighbors, setNeighbors] = useState({ prev: null, next: null, pos: 0, total: 0 });

  useEffect(() => {
    // La fiche est réutilisée d'un produit à l'autre (navigation Suivant/Précédent) :
    // sans cette remise à zéro, la quantité choisie et la confirmation d'ajout du
    // produit précédent s'afficheraient sur le suivant.
    setQuantity(1);
    setAddOutcome(null);
    (async () => {
      try {
        const data = await getProduct(id);
        setProduct(data);
      } catch {
        setProduct(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Journal de navigation local (bonus, jamais bloquant) : chaque fiche consultée
  // est enregistrée dans localStorage. Rien ne quitte le poste tant que le visiteur
  // ne clique pas un CTA rendez-vous (cf. useBrowseJournal.js et rdv-conseil-line).
  useEffect(() => {
    if (product?.id) {
      recordProductView({ id: product.id, name: product.name, price: product.price });
    }
  }, [product?.id]);

  // Navigation Suivant/Précédent : relit la requête filtres mémorisée par le catalogue
  // (sessionStorage) et récupère la liste ordonnée d'IDs (même tri que /products), pour
  // passer de fiche en fiche sans repasser par les filtres. Cf boss 2026-06-02.
  useEffect(() => {
    let active = true;
    let q = null;
    try { q = sessionStorage.getItem('catalogue_q'); } catch { /* indispo */ }
    if (q === null) { setNeighbors({ prev: null, next: null, pos: 0, total: 0 }); return; }
    const params = Object.fromEntries(new URLSearchParams(q));
    getProductIds(params)
      .then((d) => {
        if (!active) return;
        const ids = d?.ids || [];
        const i = ids.indexOf(id);
        setNeighbors(i === -1
          ? { prev: null, next: null, pos: 0, total: ids.length }
          : { prev: i > 0 ? ids[i - 1] : null, next: i < ids.length - 1 ? ids[i + 1] : null, pos: i + 1, total: ids.length });
      })
      .catch(() => {});
    return () => { active = false; };
  }, [id]);

  useEffect(() => {
    let active = true;
    const loadWishlistState = async () => {
      if (!user) {
        if (active) setIsFavorite(false);
        return;
      }
      try {
        const data = await getWishlist();
        if (!active) return;
        setIsFavorite((data.items || []).some((item) => item.product.id === id));
      } catch {
        if (active) setIsFavorite(false);
      }
    };
    loadWishlistState();
    return () => { active = false; };
  }, [id, user]);

  if (loading) return (
    <div className="bg-primary flex items-center justify-center py-32 min-h-screen">
      <Loader2 className="w-8 h-8 text-secondary animate-spin" />
    </div>
  );

  if (!product) return (
    <div className="bg-primary text-white max-w-6xl mx-auto px-4 py-16 text-center min-h-[60vh]">
      <h2 className="font-heading font-bold text-2xl uppercase tracking-tight mb-4">Produit non trouvé</h2>
      <Link to="/catalogue" className="text-secondary hover:text-secondary/80 font-bold uppercase tracking-wider text-sm">Retour au catalogue</Link>
    </div>
  );

  const status = STATUS_MAP[product.status] || STATUS_MAP.available;
  const images = product.images?.length > 0 ? product.images : [];
  const isSold = product.status === 'sold';
  const isReserved = product.status === 'reserved';
  const isAvailable = product.status === 'available';
  const isMaintenanceActive = Boolean(maintenance?.enabled);
  // Garde par PERMISSION plutôt que par liste de rôles en dur : la liste divergeait de
  // la source de vérité (config.py) à chaque ajout de rôle, et le reste du front passe
  // déjà par hasPermission.
  const isAdmin = !!user && hasPermission('products:manage');

  const handleSetSaleStatus = async (newStatus) => {
    if (statusSaving || product.status === newStatus) return;
    setStatusSaving(true);
    setStatusError(null);
    try {
      const updated = await setProductSaleStatus(product.id || id, newStatus);
      setProduct((prev) => ({ ...prev, ...updated }));
    } catch (err) {
      setStatusError(err.message || 'Échec de la mise à jour du statut');
      setTimeout(() => setStatusError(null), 5000);
    } finally {
      setStatusSaving(false);
    }
  };

  const specs = [
    product.processor && { icon: Cpu, label: 'CPU', value: product.processor },
    product.ram && { icon: MemoryStick, label: 'RAM', value: product.ram },
    product.storage && { icon: HardDrive, label: 'Stockage', value: product.storage },
    product.screen_size && { icon: MonitorIcon, label: 'Écran', value: product.screen_size },
  ].filter(Boolean);

  const availability = product.availability;
  const dMin = product.delivery_days_min;
  const dMax = product.delivery_days_max;

  // Formulations POSITIVES et vertes : un produit disponible est commandable, on ne
  // veut aucune mention anxiogène (« bientôt », « approvisionnement »). Le délai éventuel
  // est présenté comme un engagement clair (« Disponible sous N jours »).
  const getAvailabilityDisplay = () => {
    if (availability === 'stock_meteris') {
      return { label: 'En stock — retrait immédiat', cls: 'text-emerald-300', bgCls: 'bg-emerald-500/10 border-emerald-500/30', icon: PackageCheck };
    }
    if (availability === 'stock_fournisseur') {
      const days = dMax || dMin;
      return { label: `Disponible sous ${days ? `${days} jours` : 'quelques jours'}`, cls: 'text-emerald-300', bgCls: 'bg-emerald-500/10 border-emerald-500/30', icon: Truck };
    }
    if (availability === 'sur_commande') {
      if (dMax && dMax >= 30) {
        return { label: 'Sur commande', cls: 'text-slate-300', bgCls: 'bg-white/5 border-white/20', icon: FileText };
      }
      const days = dMax || dMin;
      return { label: `Disponible sous ${days ? `${days} jours` : 'quelques jours'}`, cls: 'text-emerald-300', bgCls: 'bg-emerald-500/10 border-emerald-500/30', icon: Truck };
    }
    return null;
  };

  const availabilityInfo = getAvailabilityDisplay();

  // Un seul parcours d'achat : le panier. Seul le devis reste à part — au-delà
  // de 30 jours de délai, ni le prix ni la disponibilité ne sont assez fiables
  // pour être encaissés, et un devis n'est de toute façon pas une transaction.
  const getCtaType = () => {
    if (!isAvailable) return 'none';
    if (!availability) return 'cart';
    if (availability === 'stock_meteris') return 'cart';
    if (availability === 'stock_fournisseur') return 'cart';
    if (availability === 'sur_commande') {
      if (dMax && dMax >= 30) return 'quote';
      return 'cart';
    }
    return 'quote';
  };

  const ctaType = getCtaType();

  // Même borne que celle appliquée par le panier et revalidée par le serveur
  // (`cart_svc._max_qty`) : reconditionné = pièce unique, sinon le stock.
  //   - maximum à 0 → rupture annoncée : aucun ajout n'est proposé, sinon le
  //     client irait droit au refus « 0 unité(s) disponible(s) » ;
  //   - maximum à 1 → pas de sélecteur : un compteur dont les deux boutons sont
  //     désactivés n'est pas une commodité, c'est du bruit.
  const maxQty = maxQtyFor(product);
  const canAddToCart = ctaType === 'cart' && maxQty > 0;
  const isOutOfStock = ctaType === 'cart' && maxQty === 0;
  const showQtyPicker = canAddToCart && maxQty > 1;

  // `serialize_product` (backend/helpers.py) renomme `product_id` en `id` et
  // n'expose les photos que sous `images[]`. Le panier, lui, indexe sur
  // `product_id` et lit la photo sous `image` : sans cette traduction explicite,
  // `addItem` sortirait en silence et le bouton paraîtrait mort. Ordre de repli
  // identique à celui de la page panier (`Cart.js::snapshotFromProduct`) : la
  // photo déjà portée par `image` ne doit pas être écrasée par un `null`.
  //
  // `addItem` renvoie `false` quand le panier n'a pas changé. Le seul cas
  // atteignable ici est la quantité déjà au maximum : `product_id` est garanti
  // non vide (`id` de la fiche, sinon le paramètre d'URL) et le bouton n'existe
  // pas quand le maximum est nul. On ne félicite donc jamais à vide.
  const handleAddToCart = () => {
    const ajoute = addItem({
      ...product,
      product_id: product.id || id,
      image: images[0] ?? product.image ?? product.image_url ?? null,
    }, quantity);
    setAddOutcome(ajoute ? 'added' : 'max');
  };

  const handleToggleWishlist = async () => {
    if (!user) {
      navigate(`/compte/inscription?next=${encodeURIComponent(`/produit/${id}`)}`);
      return;
    }
    setFavoriteLoading(true);
    try {
      if (isFavorite) {
        await removeFromWishlist(id);
        setIsFavorite(false);
      } else {
        await addToWishlist(id);
        setIsFavorite(true);
      }
    } finally {
      setFavoriteLoading(false);
    }
  };

  const productUrl = `${BUSINESS.url}/produit/${product.id || id}`;
  const productImage = images.length > 0 ? getImageUrl(images[0]) : null;
  const seoTitle = `${product.name} — ${BUSINESS.name}`;
  const seoDescription = (product.description_long || product.description || '')
    .replace(/\s+/g, ' ').trim().slice(0, 300) || `${product.name} disponible chez ${BUSINESS.name}. ${product.condition === 'neuf' ? 'Neuf' : 'Reconditionné'}, garantie ${getDisplayWarranty(product) || 'incluse'}.`;
  const productJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description_long || product.description || product.name,
    sku: product.external_ref || product.id || id,
    ...(product.brand ? { brand: { '@type': 'Brand', name: product.brand } } : {}),
    ...(productImage ? { image: productImage } : {}),
    offers: {
      '@type': 'Offer',
      price: product.price,
      priceCurrency: 'EUR',
      availability: product.status === 'available' ? 'https://schema.org/InStock'
        : product.status === 'reserved' ? 'https://schema.org/PreOrder' : 'https://schema.org/OutOfStock',
      itemCondition: product.condition === 'neuf' ? 'https://schema.org/NewCondition' : 'https://schema.org/RefurbishedCondition',
      url: productUrl,
      seller: { '@type': 'Organization', name: BUSINESS.name, url: BUSINESS.url },
    },
  };

  // Fil d'Ariane Accueil › Catalogue › la fiche : le même que celui du rendu serveur (backend/routers/seo.py,
  // json_ld_fil_ariane). react-helmet-async remplace au montage les scripts `data-rh` du serveur : sans celui-ci, Google
  // (qui exécute le JavaScript) ne verrait plus le fil.
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: `${BUSINESS.url}/` },
      { '@type': 'ListItem', position: 2, name: 'Catalogue', item: `${BUSINESS.url}/catalogue` },
      { '@type': 'ListItem', position: 3, name: product.name || 'Produit', item: productUrl },
    ],
  };

  const specRowsDetailed = (() => {
    const raw = product.specs;
    if (!raw || typeof raw !== 'object') return [];
    const modelStr = `${raw.model || ''} ${raw.display_name || ''}`;
    const isDesktop = /\b(SFF|DT|Desktop|Tower|MT|Mini Tower|MicroTower|ProDesk|EliteDesk|OptiPlex|ThinkCentre|Aurora|Inspiron Desktop)\b/i.test(modelStr);
    const ORDERED_SPECS = [
      ['manufacturer', { label: 'Marque' }],
      ['model', { label: 'Modèle' }],
      ['serial', { label: 'Numéro de série' }],
      ['cpu', { label: 'Processeur' }],
      ['cpu_cores', { label: 'Cœurs CPU' }],
      ['ram_gb', { label: 'Mémoire RAM', suffix: ' Go' }],
      ['storage_type', { label: 'Type de stockage' }],
      ['storage_gb', { label: 'Capacité stockage', suffix: ' Go' }],
      ['gpu', { label: 'Carte graphique' }],
      ['os', { label: 'Système' }],
      ['screen_size_inches', { label: 'Taille écran', suffix: '"', hideOnDesktop: true }],
      ['screen_resolution', { label: 'Résolution écran', hideOnDesktop: true }],
      ['weight_kg', { label: 'Poids', suffix: ' kg', hideOnDesktop: true }],
      ['color', { label: 'Couleur' }],
    ];
    return ORDERED_SPECS
      .map(([key, meta]) => {
        if (meta.hideOnDesktop && isDesktop) return null;
        const v = raw[key];
        if (v === null || v === undefined || v === '') return null;
        if (typeof v === 'number' && v === 0) return null;
        return { key, label: meta.label, value: `${v}${meta.suffix || ''}` };
      })
      .filter(Boolean);
  })();

  return (
    <div data-testid="product-detail-page">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={productUrl} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={productUrl} />
        {productImage && <meta property="og:image" content={productImage} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        {productImage && <meta name="twitter:image" content={productImage} />}
        <script type="application/ld+json">{JSON.stringify(productJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      {/* ─── HERO PRODUIT (fond sombre + image contextuelle) ─── */}
      <section className="relative bg-primary text-white py-10 md:py-16 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-45"
          style={{ backgroundImage: `url('${getHeroImageForProduct(product)}')` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/70 to-primary/20" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <Link to="/catalogue" className="inline-flex items-center gap-2 text-sm font-accent uppercase tracking-wider text-slate-400 hover:text-secondary transition-colors" data-testid="back-to-catalogue">
                <ArrowLeft className="w-[15px] h-[15px]" />
                Retour au catalogue
              </Link>
              {/* Navigation entre fiches du catalogue filtré (Suivant/Précédent) — boss 2026-06-02 */}
              {neighbors.total > 1 && (
                <div className="inline-flex items-center gap-1 text-xs font-accent uppercase tracking-wider" data-testid="product-nav">
                  <Link
                    to={neighbors.prev ? `/produit/${neighbors.prev}` : '#'}
                    onClick={(e) => { if (!neighbors.prev) e.preventDefault(); }}
                    aria-disabled={!neighbors.prev}
                    className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-sm border transition-colors ${neighbors.prev ? 'border-white/20 text-white/90 hover:border-secondary hover:text-secondary' : 'border-white/10 text-white/25 cursor-not-allowed'}`}
                    data-testid="product-prev"
                  >
                    <ChevronLeft className="w-4 h-4" /><span className="hidden sm:inline">Précédent</span>
                  </Link>
                  {neighbors.pos > 0 && <span className="text-slate-400 px-1.5 tabular-nums normal-case">{neighbors.pos} / {neighbors.total}</span>}
                  <Link
                    to={neighbors.next ? `/produit/${neighbors.next}` : '#'}
                    onClick={(e) => { if (!neighbors.next) e.preventDefault(); }}
                    aria-disabled={!neighbors.next}
                    className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-sm border transition-colors ${neighbors.next ? 'border-white/20 text-white/90 hover:border-secondary hover:text-secondary' : 'border-white/10 text-white/25 cursor-not-allowed'}`}
                    data-testid="product-next"
                  >
                    <span className="hidden sm:inline">Suivant</span><ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              )}
            </div>
            {/* Barre quick-edit admin — uniquement gestionnaire+. Combine :
                lien édition back-office + transition rapide d'état commercial
                (Disponible / Réservé / Vendu) sans flow réservation client.
                Feedback boss 2026-05-29 (sale_status inline depuis fiche). */}
            {isAdmin && (product.id || product.product_id) && (
              <div className="flex flex-wrap items-center gap-2" data-testid="admin-quick-bar">
                <Link
                  to={`/my/produits/${product.id || product.product_id}/modifier`}
                  className="inline-flex items-center gap-2 text-xs font-accent uppercase tracking-wider px-3 py-1.5 rounded-sm border border-secondary/60 bg-secondary/15 text-secondary hover:bg-secondary hover:text-white transition-colors"
                  data-testid="admin-quick-edit"
                  title="Modifier ce produit dans le back-office"
                >
                  <Edit3 className="w-3.5 h-3.5" />
                  Modifier
                </Link>

                {/* Séparateur visuel */}
                <span className="hidden md:inline-block w-px h-5 bg-white/15" aria-hidden />

                <AdminStatusButton
                  active={isAvailable}
                  label="Disponible"
                  Icon={CheckCircle2}
                  activeCls="bg-emerald-500/25 text-emerald-300 border-emerald-400/60"
                  onClick={() => handleSetSaleStatus('available')}
                  disabled={statusSaving}
                />
                <AdminStatusButton
                  active={isReserved}
                  label="Réservé"
                  Icon={Clock4}
                  activeCls="bg-amber-500/25 text-amber-300 border-amber-400/60"
                  onClick={() => handleSetSaleStatus('reserved')}
                  disabled={statusSaving}
                />
                <AdminStatusButton
                  active={isSold}
                  label="Vendu"
                  Icon={XCircle}
                  activeCls="bg-rose-500/25 text-rose-300 border-rose-400/60"
                  onClick={() => handleSetSaleStatus('sold')}
                  disabled={statusSaving}
                />
              </div>
            )}
          </div>

          {/* Feedback admin erreur transition (volatile, 5s) */}
          {isAdmin && statusError && (
            <div className="mb-4 px-3 py-2 rounded-sm bg-rose-500/15 border border-rose-400/40 text-rose-200 text-sm" role="alert">
              {statusError}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">

            {/* Galerie */}
            <div data-testid="product-gallery">
              <div
                className={`product-photo-wrap ${product.condition === 'neuf' && !isAvailable ? 'product-photo-wrap--coming-soon' : ''}`}
                style={isSold ? { filter: 'grayscale(75%) brightness(0.85)' } : undefined}
              >
                {images.length > 0 ? (
                  <img src={getImageUrl(images[currentImage])} alt={product.name} />
                ) : (
                  <MonitorIcon className="w-24 h-24 text-slate-300" />
                )}

                {images.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentImage(i => (i - 1 + images.length) % images.length)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-primary/80 backdrop-blur-sm flex items-center justify-center hover:bg-primary transition-colors text-white"
                      aria-label="Image précédente"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setCurrentImage(i => (i + 1) % images.length)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-primary/80 backdrop-blur-sm flex items-center justify-center hover:bg-primary transition-colors text-white"
                      aria-label="Image suivante"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}

                {/* Bandeau RÉSERVÉ (orange, plein largeur en BAS — boss
                    2026-05-29, harmonisé avec ProductCard pour ne pas cacher
                    les badges supérieurs des fiches). */}
                {isReserved && (
                  <div
                    className="absolute bottom-0 left-0 right-0 bg-slate-800 text-white font-heading font-bold uppercase tracking-widest text-sm py-2 text-center shadow-lg pointer-events-none"
                    data-testid="reserved-banner"
                  >
                    <Clock4 className="inline-block w-4 h-4 mr-2 -mt-0.5" />
                    Réservé — en attente de retrait
                  </div>
                )}

                {/* Tampon VENDU diagonal style certificat (boss feedback : tampon + photo grisée) */}
                {isSold && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="sold-stamp">
                    <span
                      className="font-heading font-bold uppercase tracking-[0.3em] text-4xl md:text-5xl text-rose-700/90 border-4 border-rose-700/90 px-6 py-2 bg-white/85 backdrop-blur-[2px] rounded-sm shadow-xl"
                      style={{ transform: 'rotate(-12deg)' }}
                    >
                      Vendu
                    </span>
                  </div>
                )}
              </div>

              {images.length > 1 && (
                <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentImage(i)}
                      className={`shrink-0 w-20 h-20 rounded-sm overflow-hidden border-2 bg-white transition-all flex items-center justify-center p-2 ${
                        currentImage === i ? 'border-secondary' : 'border-transparent opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img src={getImageUrl(img)} alt="" className="max-w-full max-h-full object-contain" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="space-y-5">

              {/* Overline tag — cohérent avec l'état réel du produit
                  (les neufs ne sont pas en stock atelier, ils arrivent) */}
              <div className="inline-flex items-center gap-2 font-accent text-xs uppercase tracking-widest text-secondary border-l-2 border-secondary pl-3 py-1">
                <span>
                  {product.condition === 'neuf' ? 'PC neuf' : 'PC reconditionné'}
                  {product.grade && ` · grade ${product.grade}`}
                  {product.condition === 'neuf' ? '' : ' · reconditionné'}
                </span>
              </div>

              {/* Badges — pour les neufs on cache "Disponible" (trompeur :
                  pas encore réservable, seulement formulaire d'alerte) */}
              <div className="flex flex-wrap gap-2">
                {product.condition !== 'neuf' && (
                  <span className={`inline-flex items-center gap-1.5 font-accent text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-sm border ${status.cls}`}>
                    {status.label === 'Disponible' && <Check className="w-3 h-3" strokeWidth={2.5} />}
                    {status.label}
                  </span>
                )}
                <span className={`font-accent text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-sm border ${
                  product.condition === 'neuf'
                    ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                    : 'bg-secondary/15 text-secondary border-secondary/30'
                }`}>
                  {product.condition === 'neuf' ? 'Neuf' : 'Reconditionné'}
                </span>
                {product.grade && (
                  <span className="font-accent text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-sm bg-white/10 text-white border border-white/15">Grade {product.grade}</span>
                )}
              </div>

              {/* Titre — pour l'équipe (isAdmin), cliquable vers la fiche
                  Base Produits du portail support (portal_product_id poussé
                  par le dispatch portail, demande boss 2026-08-02). */}
              <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl uppercase tracking-tight leading-[1.05]" data-testid="product-name">
                {isAdmin && product.portal_product_id ? (
                  <a
                    href={`https://support.meteris.fr/gestion/produits/${product.portal_product_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-secondary transition-colors"
                    title="Ouvrir dans la Base Produits (portail support)"
                    data-testid="product-name-portal-link"
                  >
                    {product.name}
                  </a>
                ) : product.name}
              </h1>
              {/* Le lien échouait EN SILENCE : sans portal_product_id le titre redevenait
                  du texte brut, sans le moindre indice. C'est ce qui a permis à 19 fiches
                  créées d'affilée de passer inaperçues. On rend l'anomalie visible pour
                  l'équipe uniquement. */}
              {isAdmin && !product.portal_product_id && (
                <div className="mt-1 text-xs font-medium text-amber-600" data-testid="product-no-portal-link">
                  ⚠ Fiche non liée à la Base Produits — le lien vers le portail est indisponible
                </div>
              )}

              {/* Bandeaux argument commercial — Reconditionné + Réparabilité.
                  Toujours dans un grid stretch pour que leurs hauteurs soient
                  identiques (boss feedback 2026-05-28). Sur lg+ ils sont côte à
                  côte ; en dessous ils s'empilent (chacun pleine largeur). */}
              {(product.is_refurbished_by_meteris || hasRepairabilityBadge(product)) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-stretch">
                  {product.is_refurbished_by_meteris && (
                    <div className="flex items-center gap-3 rounded-sm border border-secondary/40 bg-secondary/10 px-4 py-3 min-h-[56px]" data-testid="refurbished-by-meteris-badge">
                      <Sparkles className="w-5 h-5 text-secondary shrink-0" />
                      <span className="font-heading font-semibold text-sm uppercase tracking-wider leading-snug">
                        Reconditionné dans notre atelier — Garantie commerciale 6 mois
                      </span>
                    </div>
                  )}
                  <RepairabilityBanner product={product} />
                </div>
              )}

              {/* Garantie, INCONDITIONNELLEMENT et près du badge. La mention
                  complète vivait dans la « Fiche technique », rendue seulement si
                  le produit a des caractéristiques renseignées : sur une fiche
                  d'atelier sans specs, le seul mot de garantie de toute la page
                  était « Garantie commerciale 6 mois » du badge ci-dessus, qui
                  laissait croire que c'était la seule protection du client. */}
              {getDisplayWarranty(product) && (
                <p className="text-sm text-slate-300" data-testid="product-warranty-line">
                  <span className="font-accent text-xs uppercase tracking-widest text-slate-400">Garantie</span>
                  {' · '}
                  {getDisplayWarranty(product)}
                </p>
              )}


              {/* Coming soon overlay : seulement pour un neuf INDISPONIBLE (réservé/vendu).
                  Un neuf disponible est commandable (CTA « Commander avec acompte »). */}
              {product.condition === 'neuf' && !isAvailable && (
                <ComingSoonOverlay variant="detail" />
              )}

              {/* Prix */}
              <div className="flex items-baseline gap-3 pt-2">
                <span className="font-heading font-bold text-5xl md:text-6xl text-secondary leading-none" data-testid="product-price">
                  {Math.floor(product.price)}
                  <span className="text-3xl">,{(product.price % 1).toFixed(2).slice(2)} €</span>
                </span>
                <span className="font-accent text-xs uppercase tracking-widest text-slate-400">TTC · Retrait à Meaux</span>
              </div>

              {/* Disponibilité */}
              {availabilityInfo && (
                <div className={`flex items-center gap-3 rounded-sm border px-4 py-3 ${availabilityInfo.bgCls}`}>
                  <availabilityInfo.icon className={`w-5 h-5 shrink-0 ${availabilityInfo.cls}`} />
                  <span className={`text-sm font-medium ${availabilityInfo.cls}`}>{availabilityInfo.label}</span>
                </div>
              )}

              {/* Brand */}
              {product.brand && (
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Tag className="w-4 h-4" />
                  <span>Marque : <strong className="text-white">{product.brand}</strong></span>
                  {product.external_ref && <span className="text-slate-500">· Réf : {product.external_ref}</span>}
                </div>
              )}

              {/* Formulaire d'alerte : seulement pour un neuf indisponible (réservé/vendu).
                  Un neuf DISPONIBLE affiche désormais les CTA (Commander avec acompte, etc.). */}
              {product.condition === 'neuf' && !isAvailable && (
                <ProductAlertForm product={product} />
              )}

              {/* Bloc indisponibilité — produits Réservé / Vendu reconditionnés.
                  Remplace les CTAs standards par : lien "voir similaires" +
                  formulaire waiting list (lead capture sur intention d'achat). */}
              {product.condition !== 'neuf' && !isAvailable && (
                <SoldOrReservedNotice product={product} status={product.status} />
              )}

              {/* CTAs panier/devis : reconditionnés ET neufs disponibles.
                  La disponibilité (stock vs commande) pilote le type de CTA via ctaType. */}
              <div className="flex flex-wrap items-center gap-2 pt-2">
                {isAvailable && !isMaintenanceActive && showQtyPicker && (
                  <div
                    className="inline-flex items-center rounded-sm border border-white/20 bg-white/5"
                    data-testid="product-qty"
                    role="group"
                    aria-label="Quantité à commander"
                  >
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      disabled={quantity <= 1}
                      aria-label="Diminuer la quantité"
                      data-testid="product-qty-minus"
                      className="px-3 py-3 text-white hover:bg-white/10 disabled:text-slate-500 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span
                      className="px-2 min-w-[2rem] text-center font-heading font-bold text-sm tabular-nums"
                      data-testid="product-qty-value"
                      aria-live="polite"
                    >
                      {quantity}
                    </span>
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
                      disabled={quantity >= maxQty}
                      aria-label="Augmenter la quantité"
                      data-testid="product-qty-plus"
                      className="px-3 py-3 text-white hover:bg-white/10 disabled:text-slate-500 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {isAvailable && !isMaintenanceActive && canAddToCart && (
                  <button
                    type="button"
                    onClick={handleAddToCart}
                    className="flex items-center gap-2 bg-white text-primary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider py-3 px-6 rounded-sm text-sm transition-all active:scale-95 border-2 border-secondary"
                    data-testid="add-to-cart-button"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    Ajouter au panier
                  </button>
                )}
                {isAvailable && !isMaintenanceActive && isOutOfStock && (
                  <span
                    className="inline-flex items-center gap-2 text-xs text-amber-200 bg-amber-500/10 border border-amber-400/40 rounded-sm px-4 py-3 font-accent uppercase tracking-wider"
                    data-testid="product-out-of-stock"
                  >
                    <PackageOpen className="w-4 h-4" />
                    Momentanément en rupture
                  </span>
                )}
                {isAvailable && !isMaintenanceActive && ctaType === 'quote' && (
                  <button
                    onClick={() => setShowQuoteRequest(true)}
                    className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/40 text-white font-bold uppercase tracking-wider py-3 px-5 rounded-sm text-sm transition-colors"
                    data-testid="quote-button"
                  >
                    Demander un devis
                  </button>
                )}
                {product.condition !== 'neuf' && isAvailable && isMaintenanceActive && (
                  <span className="text-xs text-slate-300 bg-white/10 border border-white/20 rounded-sm px-4 py-2 font-accent uppercase tracking-wider" data-testid="reserve-button-disabled">
                    Réservations indisponibles
                  </span>
                )}

                <a
                  href={`tel:${BUSINESS.telephoneRaw}`}
                  className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold uppercase tracking-wider py-3 px-5 rounded-sm text-sm transition-colors active:scale-95"
                  data-testid="product-call-btn"
                >
                  <Phone className="w-4 h-4" />
                  Appeler
                </a>
                <button
                  type="button"
                  onClick={handleToggleWishlist}
                  disabled={favoriteLoading}
                  className={`flex items-center gap-2 py-3 px-4 rounded-sm text-sm transition-colors ${
                    isFavorite ? 'bg-rose-500/15 text-rose-300 border border-rose-500/30' : 'bg-white/10 text-white border border-white/20 hover:bg-white/20'
                  }`}
                  aria-label={isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                >
                  {favoriteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" />}
                </button>
                <a
                  href={BUSINESS.urlGoogleMaps}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white py-3 px-4 rounded-sm text-sm transition-colors"
                  data-testid="product-directions-btn"
                  aria-label="Itinéraire"
                >
                  <Navigation className="w-4 h-4" />
                </a>
              </div>

              {isAvailable && (
                <p className="mt-3 text-sm text-slate-300" data-testid="rdv-conseil-line">
                  Pas sûr de votre choix ?{' '}
                  <a
                    href={RDV_CONSEIL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => writeJourneyCookie()}
                    className="text-secondary underline underline-offset-2 hover:no-underline"
                  >
                    Un technicien de l'atelier vous rappelle — gratuit, 10 min
                  </a>
                </p>
              )}

              {/* Confirmation d'ajout — inline, jamais une alerte navigateur.
                  Elle laisse le choix entre aller payer et poursuivre la visite,
                  sans jamais imposer la redirection vers le panier : un client
                  qui compose une commande de plusieurs postes ne doit pas être
                  sorti du catalogue à chaque ajout.
                  Tournure NEUTRE en genre : « ajouté » serait fautif sur une
                  imprimante ou une tour. */}
              {addOutcome === 'added' && (
                <div
                  className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-sm border border-emerald-500/30 bg-emerald-500/10 px-4 py-3"
                  data-testid="add-to-cart-confirmation"
                  role="status"
                >
                  {/* `basis-72` donne au message une largeur de base : sans elle,
                      `flex-1` part d'une base nulle et le texte se comprime en
                      colonne d'un mot pendant que les boutons gardent leur taille.
                      En dessous de cette largeur, c'est le groupe de boutons qui
                      passe à la ligne — jamais la phrase qui s'étrangle. */}
                  <div className="flex items-start gap-3 flex-1 basis-72 min-w-0">
                    <CheckCircle2 className="w-5 h-5 text-emerald-300 shrink-0 mt-0.5" />
                    <span className="text-sm text-emerald-200">
                      « {product.name} » est maintenant dans votre panier.
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    <Link
                      to="/panier"
                      data-testid="go-to-cart"
                      className="inline-flex items-center gap-1.5 bg-white text-primary hover:bg-secondary hover:text-white border-2 border-secondary font-bold uppercase tracking-wider py-2 px-4 rounded-sm text-xs transition-colors whitespace-nowrap"
                    >
                      Voir mon panier
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                    <Link
                      to="/catalogue"
                      data-testid="continue-shopping"
                      className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold uppercase tracking-wider py-2 px-4 rounded-sm text-xs transition-colors whitespace-nowrap"
                    >
                      Continuer mes achats
                    </Link>
                  </div>
                </div>
              )}

              {/* Ajout sans effet : la quantité maximale est déjà au panier.
                  Le dire, plutôt que d'afficher une confirmation qui laisserait
                  croire à un second exemplaire commandé. */}
              {addOutcome === 'max' && (
                <div
                  className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-sm border border-amber-400/40 bg-amber-500/10 px-4 py-3"
                  data-testid="add-to-cart-max"
                  role="status"
                >
                  <div className="flex items-start gap-3 flex-1 basis-72 min-w-0">
                    <Info className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
                    <span className="text-sm text-amber-100">
                      Votre panier contient déjà la quantité maximale disponible
                      {maxQty > 1 ? ` (${maxQty})` : ''} pour cet article.
                    </span>
                  </div>
                  <Link
                    to="/panier"
                    data-testid="go-to-cart-max"
                    className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold uppercase tracking-wider py-2 px-4 rounded-sm text-xs transition-colors shrink-0 whitespace-nowrap"
                  >
                    Voir mon panier
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              )}

              {/* Quick spec chips */}
              {specs.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-4 border-t border-white/10" data-testid="product-specs">
                  {specs.map((spec, i) => (
                    <div key={i} className="flex flex-col gap-0.5">
                      <span className="font-accent text-[10px] uppercase tracking-widest text-slate-500">{spec.label}</span>
                      <span className="font-heading text-sm font-semibold">{spec.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ─── DESCRIPTION & FICHE TECHNIQUE (fond clair) ─── */}
      {(product.description_long || product.description || specRowsDetailed.length > 0) && (
        <section className="bg-slate-50 text-slate-900 py-12 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">

            <div className="text-center mb-12">
              <span className="font-accent text-xs uppercase tracking-widest text-secondary">Le détail produit</span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight text-primary mt-2">Fiche technique &amp; description</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 md:gap-12">

              {/* Fiche technique (à GAUCHE) */}
              {specRowsDetailed.length > 0 && (
                <div className="bg-white rounded-sm shadow-xl border border-slate-200 p-6 md:p-8" data-testid="product-detailed-specs">
                  <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-primary mb-5 pb-3 border-b border-slate-200 flex items-center gap-2">
                    <span className="inline-block w-2 h-2 bg-secondary rounded-sm"></span>
                    Fiche technique
                  </h3>
                  <dl>
                    {specRowsDetailed.map((row) => (
                      <div key={row.key} className="spec-row">
                        <dt>{row.label}</dt>
                        <dd>{row.value}</dd>
                      </div>
                    ))}
                    {getDisplayWarranty(product) && (
                      <div className="spec-row"><dt>Garantie</dt><dd>{getDisplayWarranty(product)}</dd></div>
                    )}
                  </dl>
                </div>
              )}

              {/* Description (à DROITE) — rendu Markdown.
                  Le portail génère la description en Markdown (titres ###,
                  **gras**, listes -). Rétrocompat texte brut : ReactMarkdown
                  rend les paragraphes simples comme du texte normal. */}
              {(product.description_long || product.description) && (
                <div className="space-y-4" data-testid="product-description">
                  <h3 className="font-heading text-sm uppercase tracking-widest text-slate-500 mb-2">Présentation</h3>
                  <div className="text-slate-700 leading-relaxed space-y-3">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        h3: ({ node, ...props }) => (
                          <h4 className="font-heading text-base font-bold text-primary uppercase tracking-wider mt-4 mb-1" {...props} />
                        ),
                        p: ({ node, ...props }) => (
                          <p className="text-slate-700 leading-relaxed" {...props} />
                        ),
                        ul: ({ node, ...props }) => (
                          <ul className="list-disc list-inside space-y-1 my-2 text-slate-700" {...props} />
                        ),
                        ol: ({ node, ...props }) => (
                          <ol className="list-decimal list-inside space-y-1 my-2 text-slate-700" {...props} />
                        ),
                        strong: ({ node, ...props }) => (
                          <strong className="font-semibold text-primary" {...props} />
                        ),
                        a: ({ node, ...props }) => (
                          <a className="text-secondary hover:underline" target="_blank" rel="noopener noreferrer" {...props} />
                        ),
                      }}
                    >
                      {product.description_long || product.description}
                    </ReactMarkdown>
                  </div>

                  {product.is_refurbished_by_meteris && product.certification_pdf_url && (
                    <a
                      href={product.certification_pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-4 text-sm font-bold uppercase tracking-wider text-secondary hover:text-primary transition-colors"
                      data-testid="refurbish-certificate-link"
                    >
                      <FileDown className="w-4 h-4" />
                      Télécharger le certificat de reconditionnement (PDF)
                    </a>
                  )}

                  {product.video_urls && product.video_urls.length > 0 && (
                    <div className="mt-4 space-y-3">
                      <span className="font-heading text-sm uppercase tracking-widest text-slate-500">Vidéo produit</span>
                      {product.video_urls.map((vUrl, vi) => (
                        <video key={vi} src={vUrl} controls className="w-full rounded-sm border border-slate-200" preload="metadata" />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ─── RECONDITIONNEMENT — protocole METERIS complet ─── */}
      {product.is_refurbished_by_meteris && (
        <section className="bg-white text-slate-900 py-12 md:py-20 border-t border-slate-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <span className="font-accent text-xs uppercase tracking-widest text-secondary">Notre protocole de reconditionnement</span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight text-primary mt-2">Plus de 70 contrôles en 4 phases</h2>
              <p className="text-slate-600 mt-3 max-w-2xl mx-auto">Chaque PC qui sort de notre atelier suit un protocole de reconditionnement complet. Aucun raccourci, aucun composant fatigué laissé en place.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[
                {
                  n: '01',
                  t: 'Tests complets',
                  d: 'Audit hardware exhaustif via outils de diagnostic professionnels — plus de 70 points de contrôle.',
                  items: ['Processeur · GPU · mémoire RAM', 'SSD/HDD · état SMART · cycles', 'Batterie · taux d\'usure', 'Écran · pixels · luminosité', 'Clavier · ports USB · audio · webcam', 'Alimentation · ventilation · charnières'],
                },
                {
                  n: '02',
                  t: 'Remise à état',
                  d: 'Tous les composants en fin de cycle sont remplacés par du matériel neuf ou éprouvé. Pas de mauvaise surprise pour le client.',
                  items: ['SSD remplacé si cycle dépassé', 'RAM upgrade ou changement si défectueuse', 'Batterie neuve si usure > 30 %', 'Pâte thermique CPU/GPU refaite', 'Dépoussiérage soufflerie technique'],
                },
                {
                  n: '03',
                  t: 'Vérifications finales',
                  d: 'Une fois remis à neuf, le PC passe une batterie de tests sous charge réelle pour valider la stabilité.',
                  items: ['Stress test CPU/GPU 30 min', 'MemTest86 — zéro erreur', 'Contrôle températures sous charge', 'Test cycle batterie complet', 'Validation autonomie réelle'],
                },
                {
                  n: '04',
                  t: 'Certification',
                  d: 'Système prêt à l\'emploi, livré avec un dossier complet et une garantie pièces + main-d\'œuvre.',
                  items: ['Windows 11 Pro activé · à jour', 'Configuration utilisateur propre', 'Certificat PDF détaillé', 'QR code de traçabilité', 'Garantie commerciale 6 mois'],
                },
              ].map((step) => (
                <div key={step.n} className="bg-slate-50 border border-slate-200 rounded-sm p-6 md:p-7 hover:border-secondary transition-colors">
                  <div className="flex items-baseline gap-4 mb-4">
                    <div className="font-heading text-5xl font-bold text-secondary leading-none">{step.n}</div>
                    <h3 className="font-heading text-xl uppercase tracking-wider text-primary">{step.t}</h3>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed mb-4">{step.d}</p>
                  <ul className="space-y-1.5">
                    {step.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-slate-600 font-medium">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-secondary shrink-0 mt-0.5">
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Accompagnement post-vente — sub-section */}
            <div className="mt-12 md:mt-16 pt-10 md:pt-12 border-t border-slate-200">
              <div className="text-center mb-8 md:mb-10">
                <span className="font-accent text-xs uppercase tracking-widest text-secondary">Au-delà du matériel</span>
                <h3 className="font-heading font-bold text-2xl md:text-3xl uppercase tracking-tight text-primary mt-2">Accompagnement inclus à la livraison</h3>
                <p className="text-slate-600 mt-3 max-w-xl mx-auto">Pour que votre nouvel ordinateur soit prêt dès le premier jour.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-secondary/5 border border-secondary/20 rounded-sm p-6 flex gap-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-secondary shrink-0">
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                    <polyline points="16 6 12 2 8 6" />
                    <line x1="12" y1="2" x2="12" y2="15" />
                  </svg>
                  <div>
                    <h4 className="font-heading text-base uppercase tracking-wider text-primary mb-1">Transfert de vos données</h4>
                    <p className="text-sm text-slate-600 leading-relaxed">Documents, photos, mails, favoris navigateurs. On récupère tout depuis votre ancien ordinateur et on l'installe sur le nouveau.</p>
                  </div>
                </div>
                <div className="bg-secondary/5 border border-secondary/20 rounded-sm p-6 flex gap-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-secondary shrink-0">
                    <rect x="2" y="3" width="20" height="14" rx="2" />
                    <line x1="8" y1="21" x2="16" y2="21" />
                    <line x1="12" y1="17" x2="12" y2="21" />
                    <path d="m9 10 2 2 4-4" />
                  </svg>
                  <div>
                    <h4 className="font-heading text-base uppercase tracking-wider text-primary mb-1">Mise en service au retrait</h4>
                    <p className="text-sm text-slate-600 leading-relaxed">À la remise en boutique, on vous montre les bases : démarrer l'ordinateur, vous connecter à internet, ouvrir vos applications. Vous repartez avec un ordinateur prêt à l'emploi.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ─── CTA RETRAIT (fond cyan) ─── */}
      <section className="bg-secondary text-white py-10 md:py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <span className="font-accent text-xs uppercase tracking-widest text-white/70">Retrait en boutique</span>
              <h2 className="font-heading font-bold text-2xl md:text-3xl uppercase tracking-tight mt-1">Récupérez votre PC à Meaux</h2>
              <p className="text-white/90 mt-2 text-sm">
                {BUSINESS.adresse} — à côté du E-Leclerc<br />
                Créneau retrait : <strong>{BUSINESS.creneauRetrait}</strong>
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <a href={`tel:${BUSINESS.telephoneRaw}`} className="cta-pickup flex items-center gap-2 bg-white text-primary hover:bg-primary hover:text-white font-bold uppercase tracking-wider rounded-sm transition-colors border border-white">
                <Phone className="w-3.5 h-3.5" />
                {BUSINESS.telephone}
              </a>
              <a href={BUSINESS.urlGoogleMaps} target="_blank" rel="noopener noreferrer" className="cta-pickup flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/40 text-white font-bold uppercase tracking-wider rounded-sm transition-colors">
                <Navigation className="w-3.5 h-3.5" />
                Itinéraire
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Modals — la fiche n'ouvre plus de tunnel d'achat : réservation et
          commande passent désormais par le panier (`/panier`), qui monte
          `SpecialOrderModal` avec toutes les lignes. Seul le devis reste ici. */}
      {showQuoteRequest && (
        <QuoteRequestModal
          product={product}
          onClose={() => setShowQuoteRequest(false)}
        />
      )}
    </div>
  );
}

// Labels FR + ordre canonique des sous-scores. Synchronisés avec ce que
// l'admin du back-office expose dans ProductForm. Si un score futur est
// ajouté côté admin sans être listé ici, il sera ignoré (pas grave : le
// score moyen reste calculé sur Object.values).
const REPAIRABILITY_LABELS = [
  ['documentation',  'Documentation technique',  'Manuels et schémas disponibles'],
  ['demontabilite',  'Démontabilité',            'Facilité de démontage / remontage'],
  ['pieces_dispo',   'Disponibilité des pièces', 'Pièces détachées trouvables sur le marché'],
  ['prix_pieces',    'Prix des pièces',          'Pièces abordables vs prix du produit'],
  ['logiciel',       'Support logiciel',         'Durée du support OS / pilotes'],
];

// Helper exporté pour le parent qui décide d'afficher la grille bandeaux.
// Réplique la logique d'affichage de RepairabilityBanner (seuil 7 + scored).
function hasRepairabilityBadge(product) {
  if (!product || product.repairability_status !== 'scored' || !product.repairability_scores) return false;
  const vals = Object.values(product.repairability_scores).filter(v => typeof v === 'number');
  if (vals.length === 0) return false;
  const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
  return avg >= 7;
}

// ── Bouton transition d'état admin (style chip pill) ──────────────────────
// État actif = couleur sémantique (vert/orange/rouge). État inactif = chip
// neutre cliquable. `disabled || active` empêche le double-clic et clarifie
// que l'état courant n'est pas une action.
function AdminStatusButton({ active, label, Icon, activeCls, onClick, disabled }) {
  const base = 'inline-flex items-center gap-1.5 text-xs font-accent uppercase tracking-wider px-3 py-1.5 rounded-sm border transition-colors';
  const inactive = 'border-white/15 bg-white/5 text-slate-300 hover:bg-white/10 hover:border-white/30';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || active}
      className={`${base} ${active ? activeCls : inactive} ${disabled && !active ? 'opacity-50 cursor-wait' : ''}`}
      aria-pressed={active}
      data-testid={`admin-status-${label.toLowerCase()}`}
      title={active ? `État courant : ${label}` : `Marquer ${label.toLowerCase()}`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

// ── Bloc indisponibilité (Réservé / Vendu) — substitut des CTAs Réserver ──
// Pivote l'attention du visiteur vers (1) le catalogue filtré similaire,
// (2) la collecte d'email pour notification d'arrivage. Lead magnet doux.
function SoldOrReservedNotice({ product, status }) {
  const isSold = status === 'sold';
  const headline = isSold
    ? 'Cette machine est déjà vendue'
    : "Cette machine vient d'être réservée";
  const subline = isSold
    ? "Mais nous reconditionnons en permanence des PCs similaires. Recevez un email dès qu'une machine équivalente arrive en boutique."
    : "Elle peut redevenir disponible si la réservation expire avant retrait. Inscrivez-vous pour être prévenu — vous serez le premier averti.";
  const category = product.category || 'pc';
  const condition = product.condition || 'reconditionne';
  return (
    <div
      className={`rounded-sm border ${isSold ? 'border-rose-400/40 bg-rose-500/5' : 'border-amber-400/40 bg-amber-500/5'} px-5 py-4 space-y-4`}
      data-testid="sold-or-reserved-notice"
    >
      <div>
        <h3 className="font-heading font-bold text-base uppercase tracking-wider text-white mb-1">
          {headline}
        </h3>
        <p className="text-sm text-slate-300 leading-relaxed">{subline}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Link
          to={`/catalogue?category=${category}&condition=${condition}`}
          className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold uppercase tracking-wider py-2 px-4 rounded-sm text-xs transition-colors"
        >
          Voir d'autres machines similaires
          <ArrowLeft className="w-3.5 h-3.5 rotate-180" />
        </Link>
      </div>
      <WaitingListForm productId={product.id || product.product_id} />
    </div>
  );
}

// ── Formulaire waiting list (mini lead capture sur fiche indispo) ─────────
// Anti-doublon côté backend (1 entrée pending par email+produit). Le backend
// renvoie { already_registered: true } — on traite ce cas comme un succès
// pour ne pas frustrer le visiteur qui revient sur la même fiche.
function WaitingListForm({ productId }) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [phase, setPhase] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    setPhase('idle');
    setErrorMsg('');
    try {
      await joinProductWaitingList(productId, {
        email: email.trim(),
        first_name: firstName.trim(),
      });
      setPhase('success');
    } catch (err) {
      setPhase('error');
      setErrorMsg(err.message || "Échec de l'inscription, réessayez plus tard.");
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === 'success') {
    return (
      <div className="pt-3 border-t border-white/10 flex items-center gap-2 text-sm text-emerald-300" data-testid="waiting-list-success">
        <CheckCircle2 className="w-4 h-4 shrink-0" />
        <span>Inscription confirmée — nous vous écrirons dès qu'une machine similaire est prête.</span>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="pt-3 border-t border-white/10 space-y-2" data-testid="waiting-list-form">
      <p className="text-xs font-accent uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
        <Bell className="w-3.5 h-3.5" />
        Prévenez-moi quand un PC similaire arrive
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          placeholder="Prénom (optionnel)"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          className="flex-1 bg-white/10 border border-white/20 rounded-sm px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-secondary min-w-0"
          aria-label="Prénom"
        />
        <input
          type="email"
          required
          placeholder="votre@email.fr"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-[2] bg-white/10 border border-white/20 rounded-sm px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-secondary min-w-0"
          aria-label="Email"
        />
        <button
          type="submit"
          disabled={submitting || !email.trim()}
          className="inline-flex items-center justify-center bg-secondary hover:bg-secondary/90 disabled:bg-slate-600 disabled:cursor-not-allowed text-white font-bold uppercase tracking-wider text-xs px-4 py-2 rounded-sm transition-colors"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Me prévenir'}
        </button>
      </div>
      {phase === 'error' && (
        <p className="text-xs text-rose-300" role="alert">{errorMsg}</p>
      )}
    </form>
  );
}

function RepairabilityBanner({ product }) {
  const [open, setOpen] = useState(false);
  if (!hasRepairabilityBadge(product)) return null;
  const vals = Object.values(product.repairability_scores).filter(v => typeof v === 'number');
  const avg = vals.reduce((a, b) => a + b, 0) / vals.length;

  // Tier visuel : ≥ 9 = Exceptionnel, ≥ 8 = Excellent, ≥ 7 = Bon
  const tier = avg >= 9 ? 'excellent' : avg >= 8 ? 'good' : 'fair';
  const tierLabel = tier === 'excellent' ? 'Exceptionnel' : tier === 'good' ? 'Excellent' : 'Bon';

  return (
    <div className="rounded-sm border border-emerald-400/60 bg-emerald-50/80" data-testid="repairability-banner">
      {/* Header — même hauteur min que le bandeau "Reconditionné" (56px) */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 text-left px-4 py-3 min-h-[56px]"
      >
        <Sparkles className="w-5 h-5 text-emerald-700 shrink-0" aria-hidden />
        <span className="font-heading font-semibold text-sm uppercase tracking-wider text-emerald-800 leading-snug flex-1">
          Réparabilité {avg.toFixed(1)}/10 — {tierLabel}
        </span>
        <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 shrink-0">
          {open ? 'Masquer' : 'Détail'}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-3 pt-1 border-t border-emerald-300 space-y-2">
          {REPAIRABILITY_LABELS.map(([key, label, hint]) => {
            const score = product.repairability_scores[key];
            if (typeof score !== 'number') return null;
            const barColor = score >= 8 ? 'bg-emerald-500' : score >= 6 ? 'bg-amber-500' : 'bg-rose-500';
            return (
              <div key={key} className="text-xs">
                <div className="flex justify-between items-baseline mb-1">
                  <span className="font-semibold text-slate-700">{label}</span>
                  <span className="font-bold text-emerald-700">{score}/10</span>
                </div>
                <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                  <div className={`h-full ${barColor}`} style={{ width: `${score * 10}%` }} />
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>
              </div>
            );
          })}
          <div className="text-[10px] text-slate-500 italic pt-1">
            Indice évalué par notre cellule technique selon 5 critères (norme inspirée Spareka/iFixit).
          </div>
        </div>
      )}
    </div>
  );
}
